import React, { useState, useEffect, useCallback } from 'react';
import './RestockPickerModal.css';

// Return reasons for RETURNED pieces (why the fabric came back). RTO is auto-prefilled
// when the courier returned the parcel (order in RTO_RECEIVED).
const RETURN_REASONS = [
  'RTO',
  'Customer Return',
  'Damaged in Transit',
  'Wrong / Incorrect Item',
  'Size / Fit Issue',
  'Quality Issue',
  'Refused Delivery',
  'Other',
];

// B3 — unified, timing-aware restock picker. Calls B1 (/restock/candidates) for the
// system-determined restockable pieces + /restock/pieces for already-restocked pieces
// (undo). Confirms via B2 /restock/piece; undoes via /restock/undo-piece.
export default function RestockPickerModal({ open, orderId, sku, orderNumber, productImage, productName, authedFetch, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [undoing, setUndoing] = useState('');
  const [error, setError] = useState('');
  const [lineData, setLineData] = useState(null);      // B1 candidates for this line
  const [restocked, setRestocked] = useState([]);      // already-restocked pieces (undo list)
  const [dmgOn, setDmgOn] = useState({});              // pieceId -> Item Damaged? boolean
  const [dmgQty, setDmgQty] = useState({});            // pieceId -> damaged meters
  const [reason, setReason] = useState({});            // pieceId -> return reason
  const [shippedPieceId, setShippedPieceId] = useState('');

  const parse = async (res) => { try { return await res.json(); } catch { return null; } };

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true); setError(''); setLineData(null); setRestocked([]);
    setDmgOn({}); setDmgQty({}); setReason({}); setShippedPieceId('');
    try {
      const [candRes, pcRes] = await Promise.all([
        authedFetch(`/api/v9/orders/${encodeURIComponent(orderId)}/restock/candidates`),
        authedFetch(`/api/v9/orders/${encodeURIComponent(orderId)}/restock/pieces?sku=${encodeURIComponent(sku || '')}`),
      ]);
      const cand = await parse(candRes);
      const pc = await parse(pcRes);
      if (pcRes.ok && pc?.ok) setRestocked(pc.result?.pieces || []);
      if (candRes.ok && cand?.ok) {
        const lines = cand.result?.lines || [];
        const match = sku ? lines.find((l) => String(l.sku).toUpperCase() === String(sku).toUpperCase()) : lines[0];
        if (match) {
          setLineData(match);
          // prefill RTO reason for returned pieces when courier returned the parcel
          const initReason = {};
          (match.entries || []).forEach((e) => { if (e.role === 'RETURNED') initReason[e.suggestedPieceId] = match.rto ? 'RTO' : ''; });
          setReason(initReason);
        }
      }
      if ((!candRes.ok || !cand?.ok) && (!pcRes.ok || !pc?.ok)) {
        setError(cand?.error?.message || 'Failed to load restock data.');
      }
    } catch (e) { setError(e?.message || 'Failed to load.'); }
    finally { setLoading(false); }
  }, [authedFetch, orderId, sku]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const submit = useCallback(async () => {
    if (!lineData || submitting) return;
    // block confirm if a returned piece has no reason chosen
    const missing = (lineData.entries || []).some((e) => e.role === 'RETURNED' && !String(reason[e.suggestedPieceId] || '').trim());
    if (missing) { setError('Select a return reason for each returned piece.'); return; }
    setSubmitting(true); setError('');
    try {
      const entries = (lineData.entries || []).map((e) => ({
        sku: lineData.sku,
        suggestedPieceId: e.suggestedPieceId,
        meters: e.meters,
        role: e.role === 'RETURNED' ? 'RETURNED' : 'SURPLUS',
        origin: e.origin,
        sourceBatchId: e.sourceBatchId || undefined,
        sourcePieceId: e.sourcePieceId || undefined,
        returnReason: e.role === 'RETURNED' ? (reason[e.suggestedPieceId] || undefined) : undefined,
        damagedQty: dmgOn[e.suggestedPieceId] ? Math.max(0, Math.min(Number(dmgQty[e.suggestedPieceId] || 0), e.meters)) : 0,
      }));
      const body = { entries };
      if (lineData.determination === 'PICKER' && shippedPieceId) body.shippedPieceId = shippedPieceId;
      const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(orderId)}/restock/piece`, {
        method: 'POST', body: JSON.stringify(body),
      });
      const data = await parse(res);
      if (!res.ok || !data?.ok) { setError(data?.error?.message || 'Restock failed.'); setSubmitting(false); return; }
      setSubmitting(false);
      if (onDone) await onDone(data.result);
      if (onClose) onClose();
    } catch (e) { setError(e?.message || 'Restock failed.'); setSubmitting(false); }
  }, [lineData, submitting, dmgOn, dmgQty, reason, shippedPieceId, authedFetch, orderId, onDone, onClose]);

  const undo = useCallback(async (pieceId) => {
    if (undoing) return;
    setUndoing(pieceId); setError('');
    try {
      const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(orderId)}/restock/undo-piece`, {
        method: 'POST', body: JSON.stringify({ sku, pieceId }),
      });
      const data = await parse(res);
      if (!res.ok || !data?.ok) { setError(data?.error?.message || 'Undo failed.'); setUndoing(''); return; }
      if (onDone) await onDone(data.result);
      await load(); // refresh both lists in place
    } catch (e) { setError(e?.message || 'Undo failed.'); }
    finally { setUndoing(''); }
  }, [undoing, authedFetch, orderId, sku, onDone, load]);

  if (!open) return null;
  const entries = lineData?.entries || [];
  const isPicker = lineData?.determination === 'PICKER';
  const totalMeters = entries.reduce((s, e) => s + Number(e.meters || 0), 0);

  return (
    <div className="rpm-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="rpm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rpm-header">
          <h3>Restock</h3>
          <button className="rpm-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        {loading && <div className="rpm-body">Loading pieces…</div>}
        {!loading && error && <div className="rpm-body"><div className="rpm-error">{error}</div></div>}

        {!loading && (
          <div className="rpm-body">
            {/* Card head — product image + SKU + order */}
            <div className="rpm-head">
              <div className="rpm-img-wrap">
                {productImage ? <img className="rpm-img" src={productImage} alt={productName || sku} /> : <div className="rpm-img-ph">No image</div>}
              </div>
              <div className="rpm-head-main">
                <div className="rpm-sku">{sku}</div>
                <div className="rpm-order">Order: {orderNumber || orderId}</div>
                {lineData && (
                  <div className="rpm-chips">
                    <span>Required {lineData.netRequiredQty}m</span>
                    <span>Shipped {lineData.shippedQty}m</span>
                    <span>To restock {Number(totalMeters.toFixed(2))}m</span>
                  </div>
                )}
              </div>
            </div>

            {isPicker && (lineData?.pieces || []).length > 0 && (
              <div className="rpm-picker">
                <div className="rpm-picker-note">
                  ℹ️ Multiple pieces were in hand at ship — select the piece that was <b>shipped to the customer</b>:
                </div>
                {(lineData.pieces || []).map((p, i) => {
                  const pid = p.origin === 'STOCK' ? (p.pieceId || `stock-${i}`) : (p.batchId || `batch-${i}`);
                  return (
                    <label key={pid} className="rpm-picker-opt">
                      <input type="radio" name="shipped" value={pid}
                        checked={shippedPieceId === pid} onChange={() => setShippedPieceId(pid)} />
                      {p.origin === 'STOCK' ? `Stock ${p.pieceId || ''}` : `Batch ${p.batchId || ''}`} · {p.meters}m
                    </label>
                  );
                })}
              </div>
            )}

            {/* Restockable pieces */}
            {entries.map((e) => {
              const pid = e.suggestedPieceId;
              const on = !!dmgOn[pid];
              const dmg = on ? Math.max(0, Math.min(Number(dmgQty[pid] || 0), e.meters)) : 0;
              const toStock = Number((e.meters - dmg).toFixed(2));
              const isReturn = e.role === 'RETURNED';
              return (
                <div key={pid} className="rpm-piece">
                  <div className="rpm-piece-top">
                    <span className={`rpm-badge ${isReturn ? 'ret' : 'exc'}`}>{isReturn ? 'Returned' : 'Excess'}</span>
                    <span className="rpm-meters">{e.meters}m</span>
                    <span className="rpm-origin">{e.origin === 'STOCK' ? `From Stock No: ${e.sourcePieceId || ''}` : `From Batch No: ${e.sourceBatchId || ''}`}</span>
                  </div>
                  <div className="rpm-why">{isReturn ? 'Shipped then came back' : 'Never shipped — surplus'}</div>

                  <div className="rpm-field">
                    <label>Piece ID (auto)</label>
                    <input readOnly value={pid} />
                  </div>

                  {isReturn && (
                    <div className="rpm-field">
                      <label>Return reason</label>
                      <select value={reason[pid] || ''} onChange={(ev) => setReason((r) => ({ ...r, [pid]: ev.target.value }))}>
                        <option value="">Select reason…</option>
                        {RETURN_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  )}

                  <div className="rpm-dmg-row">
                    <span className="rpm-dmg-label">Item Damaged?</span>
                    <button type="button" className={`rpm-toggle ${on ? 'on' : ''}`} role="switch" aria-checked={on}
                      onClick={() => setDmgOn((s) => ({ ...s, [pid]: !s[pid] }))}>
                      <span className="rpm-knob" />
                    </button>
                  </div>
                  {on && (
                    <div className="rpm-field">
                      <label>Damaged meters (of {e.meters}m)</label>
                      <input type="number" min="0" max={e.meters} step="0.5"
                        value={dmgQty[pid] ?? ''} placeholder="0"
                        onChange={(ev) => setDmgQty((q) => ({ ...q, [pid]: ev.target.value }))} />
                    </div>
                  )}

                  <div className="rpm-outcome">
                    <div>→ <b>{toStock}m</b> to stock <span className="rpm-dmg-pid">({pid})</span></div>
                    {dmg > 0 && <div className="rpm-outcome-dmg">→ <b>{dmg}m</b> damaged <span className="rpm-dmg-pid">({pid}-D)</span></div>}
                  </div>
                </div>
              );
            })}

            {!entries.length && lineData && <div className="rpm-empty">Nothing pending to restock on this line.</div>}

            {/* Already-restocked pieces (undo) */}
            {restocked.length > 0 && (
              <div className="rpm-undo">
                <div className="rpm-undo-head">Already restocked</div>
                {restocked.map((p) => (
                  <div key={p.pieceId} className="rpm-undo-row">
                    <div className="rpm-undo-main">
                      <span className={`rpm-badge ${p.role === 'RETURNED' ? 'ret' : 'exc'}`}>{p.role === 'RETURNED' ? 'Returned' : 'Excess'}</span>
                      <b className="rpm-undo-pid">{p.pieceId}</b>
                      <span className="rpm-undo-meta">
                        {p.activeMeters}m{p.damagedMeters > 0 ? ` + ${p.damagedMeters}m dmg` : ''}
                        {p.returnReason ? ` · ${p.returnReason}` : ''}
                        {p.restockedBy ? ` · ${p.restockedBy}` : ''}
                      </span>
                    </div>
                    <button className="rpm-undo-btn" disabled={!!undoing} onClick={() => undo(p.pieceId)}>
                      {undoing === p.pieceId ? 'Undoing…' : 'Undo'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="rpm-footer">
          <button className="rpm-btn" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="rpm-btn primary" onClick={submit}
            disabled={submitting || loading || !entries.length || (isPicker && !shippedPieceId)}>
            {submitting ? 'Restocking…' : `Confirm Restock (${entries.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
